import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single in-flight refresh promise so parallel 401s share one refresh request.
// Without this, every concurrent failed request triggers its own refresh,
// hitting a backend race condition on refresh-token rotation that wipes the
// session ("No token provided" after the first refresh).
let refreshPromise: Promise<string> | null = null;

const performRefresh = async (refreshToken: string): Promise<string> => {
  const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data.data;
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  return accessToken;
};

// Response interceptor with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip refresh logic for auth endpoints to prevent loops
    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          if (!refreshPromise) {
            refreshPromise = performRefresh(refreshToken).finally(() => {
              refreshPromise = null;
            });
          }
          const newAccessToken = await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          // Hard redirect so user lands on /login with a clean state.
          // window.location avoids React Router needing to know about this.
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token but got 401 — definitively logged out.
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) =>
    apiClient.post('/auth/logout', { refreshToken }),
  me: () => apiClient.get('/auth/me'),
  applyOrganization: (data: any) =>
    apiClient.post('/auth/apply-organization', data),
  verifyResetToken: (token: string) =>
    apiClient.get(`/auth/verify-reset-token/${token}`),
  verifyCode: (email: string, code: string) =>
    apiClient.post('/auth/verify-code', { email, code }),
  setPassword: (token: string, password: string) =>
    apiClient.post('/auth/set-password', { token, password }),
};

// Admin API
export const adminApi = {
  getOrganizations: (params?: any) =>
    apiClient.get('/admin/organizations', { params }),
  getPendingApplications: () =>
    apiClient.get('/admin/applications'),
  getOrganization: (id: string) =>
    apiClient.get(`/admin/organizations/${id}`),
  approveOrganization: (id: string, trialDays?: number) =>
    apiClient.post(`/admin/organizations/${id}/approve`, { trialDays }),
  rejectOrganization: (id: string) =>
    apiClient.post(`/admin/organizations/${id}/reject`),
  suspendOrganization: (id: string) =>
    apiClient.post(`/admin/organizations/${id}/suspend`),
  activateOrganization: (id: string) =>
    apiClient.post(`/admin/organizations/${id}/activate`),
  updateOrganization: (id: string, data: any) =>
    apiClient.put(`/admin/organizations/${id}`, data),
  setTrialPeriod: (id: string, startDate: string, endDate: string) =>
    apiClient.put(`/admin/organizations/${id}/trial`, { startDate, endDate }),
  getStats: () =>
    apiClient.get('/admin/stats'),
  searchCvr: (query: string) =>
    apiClient.get('/admin/cvr/search', { params: { q: query } }),
  createOrganization: (data: {
    name: string;
    cvr?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    seedDemoData?: boolean;
    adminEmail: string;
    adminFirstName?: string;
    adminLastName?: string;
    adminPhone?: string;
  }) => apiClient.post('/admin/organizations', data),
  resetUserPassword: (userId: string) =>
    apiClient.post(`/admin/users/${userId}/reset-password`),
  sendSetupEmail: (userId: string) =>
    apiClient.post(`/admin/users/${userId}/send-setup-email`),
  getFlowtaleDocs: () =>
    apiClient.get('/admin/flowtale-docs'),
  getTmsLogSummary: (params?: { days?: number }) =>
    apiClient.get('/admin/tms-log/summary', { params }),
  getTmsLogRuns: (params?: any) =>
    apiClient.get('/admin/tms-log/runs', { params }),
  getTmsLogEvents: (params?: any) =>
    apiClient.get('/admin/tms-log/events', { params }),
  createUserForOrganization: (organizationId: string, data: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    role?: 'ADMIN' | 'USER';
  }) => apiClient.post(`/admin/organizations/${organizationId}/users`, data),
};

// Customer API
export const customerApi = {
  getAll: (params?: any) =>
    apiClient.get('/customers', { params }),
  getById: (id: string) =>
    apiClient.get(`/customers/${id}`),
  create: (data: any) =>
    apiClient.post('/customers', data),
  update: (id: string, data: any) =>
    apiClient.put(`/customers/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/customers/${id}`),
  updateStatus: (id: string, status: string) =>
    apiClient.put(`/customers/${id}/status`, { status }),
  assign: (id: string, assignedToId: string | null) =>
    apiClient.put(`/customers/${id}/assign`, { assignedToId }),
  bulkAssign: (customerIds: string[], assignedToId: string | null) =>
    apiClient.post('/customers/bulk-assign', { customerIds, assignedToId }),
  getStats: () =>
    apiClient.get('/customers/stats'),
  getIndustries: () =>
    apiClient.get('/customers/industries'),
  getCountries: () =>
    apiClient.get('/customers/countries'),
  exportCsv: () =>
    apiClient.get('/customers/export', { responseType: 'blob' }),
  getContacts: (id: string) =>
    apiClient.get(`/customers/${id}/contacts`),
  createContact: (id: string, data: any) =>
    apiClient.post(`/customers/${id}/contacts`, data),
  addContact: (id: string, data: any) =>
    apiClient.post(`/customers/${id}/contacts`, data),
  getActivities: (id: string, params?: any) =>
    apiClient.get(`/customers/${id}/activities`, { params }),
  getOffers: (id: string, params?: any) =>
    apiClient.get(`/customers/${id}/offers`, { params }),
  createOffer: (customerId: string, data: any) =>
    apiClient.post(`/customers/${customerId}/offers`, data),
  updateOffer: (customerId: string, offerId: string, data: any) =>
    apiClient.put(`/customers/${customerId}/offers/${offerId}`, data),
  updateOfferStatus: (customerId: string, offerId: string, status: string) =>
    apiClient.put(`/customers/${customerId}/offers/${offerId}/status`, { status }),
  deleteOffer: (customerId: string, offerId: string) =>
    apiClient.delete(`/customers/${customerId}/offers/${offerId}`),
  getTransports: (id: string, params?: any) =>
    apiClient.get(`/customers/${id}/transports`, { params }),
  getGeography: (id: string) =>
    apiClient.get(`/customers/${id}/geography`),
  getChildren: (id: string) =>
    apiClient.get(`/customer-hierarchy/${id}/children`),
  getGroupStats: (id: string) =>
    apiClient.get(`/customer-hierarchy/${id}/group-stats`),
  setParent: (id: string, parentId: string) =>
    apiClient.post(`/customer-hierarchy/${id}/set-parent`, { parentId }),
  removeParent: (id: string) =>
    apiClient.post(`/customer-hierarchy/${id}/remove-parent`),
  updateContact: (customerId: string, contactId: string, data: any) =>
    apiClient.put(`/customers/${customerId}/contacts/${contactId}`, data),
  deleteContact: (customerId: string, contactId: string) =>
    apiClient.delete(`/customers/${customerId}/contacts/${contactId}`),
};

// Activity API
export const activityApi = {
  getById: (id: string) =>
    apiClient.get(`/activities/${id}`),
  getByCustomer: (customerId: string, params?: any) =>
    apiClient.get(`/customers/${customerId}/activities`, { params }),
  create: (data: any) =>
    apiClient.post('/activities', data),
  update: (id: string, data: any) =>
    apiClient.put(`/activities/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/activities/${id}`),
  getRecent: (limit?: number) =>
    apiClient.get('/activities/recent', { params: { limit } }),
  addComment: (id: string, content: string) =>
    apiClient.post(`/activities/${id}/comments`, { content }),
  deleteComment: (activityId: string, commentId: string) =>
    apiClient.delete(`/activities/${activityId}/comments/${commentId}`),
};

// User API
export const userApi = {
  getAll: (params?: any) =>
    apiClient.get('/users', { params }),
  getById: (id: string) =>
    apiClient.get(`/users/${id}`),
  create: (data: any) =>
    apiClient.post('/users', data),
  update: (id: string, data: any) =>
    apiClient.put(`/users/${id}`, data),
  updatePassword: (id: string, password: string) =>
    apiClient.put(`/users/${id}/password`, { password }),
  deactivate: (id: string) =>
    apiClient.post(`/users/${id}/deactivate`),
  activate: (id: string) =>
    apiClient.post(`/users/${id}/activate`),
  delete: (id: string) =>
    apiClient.delete(`/users/${id}`),
  getDispatchers: () =>
    apiClient.get('/users/dispatchers'),
  sendSetupEmail: (id: string) =>
    apiClient.post(`/users/${id}/send-setup-email`),
};

// Task API
export const taskApi = {
  getAll: (params?: any) =>
    apiClient.get('/tasks', { params }),
  getToday: () =>
    apiClient.get('/tasks/today'),
  getOverdue: () =>
    apiClient.get('/tasks/overdue'),
  getByCustomer: (customerId: string, params?: any) =>
    apiClient.get(`/customers/${customerId}/tasks`, { params }),
  getById: (id: string) =>
    apiClient.get(`/tasks/${id}`),
  create: (data: any) =>
    apiClient.post('/tasks', data),
  update: (id: string, data: any) =>
    apiClient.put(`/tasks/${id}`, data),
  complete: (id: string) =>
    apiClient.post(`/tasks/${id}/complete`),
  uncomplete: (id: string) =>
    apiClient.post(`/tasks/${id}/uncomplete`),
  delete: (id: string) =>
    apiClient.delete(`/tasks/${id}`),
};

// Settings API
export const settingsApi = {
  get: () =>
    apiClient.get('/settings'),
  update: (data: any) =>
    apiClient.put('/settings', data),
  getSegments: () =>
    apiClient.get('/settings/segments'),
  createSegment: (data: any) =>
    apiClient.post('/settings/segments', data),
  updateSegment: (id: string, data: any) =>
    apiClient.put(`/settings/segments/${id}`, data),
  deleteSegment: (id: string) =>
    apiClient.delete(`/settings/segments/${id}`),
  getSegmentGeoOptions: () =>
    apiClient.get('/settings/segments/geo-options'),
  applySegment: (id: string) =>
    apiClient.post(`/settings/segments/${id}/apply`),
  getTags: () =>
    apiClient.get('/settings/tags'),
  createTag: (data: any) =>
    apiClient.post('/settings/tags', data),
  updateTag: (id: string, data: any) =>
    apiClient.put(`/settings/tags/${id}`, data),
  deleteTag: (id: string) =>
    apiClient.delete(`/settings/tags/${id}`),
  testTeamsWebhook: (webhookUrl: string) =>
    apiClient.post('/settings/teams/test', { webhookUrl }),
};

// TMS API
export const tmsApi = {
  getSyncStatus: () =>
    apiClient.get('/tms/sync-status'),
  syncCustomers: () =>
    apiClient.post('/tms/sync-customers'),
  triggerSync: () =>
    apiClient.post('/tms/sync-customers'),
  syncAll: () =>
    apiClient.post('/tms/sync-all'),
  testConnection: () =>
    apiClient.post('/tms/test-connection'),
  getCustomerLiveData: (customerId: string, region = 'dk') =>
    apiClient.get(`/tms/customer/${customerId}/live-data`, { params: { region } }),
  getCustomerMappings: (params?: any) =>
    apiClient.get('/tms/customer-mappings', { params }),
  getUnmatchedCustomers: (params?: any) =>
    apiClient.get('/tms/unmatched-customers', { params }),
  linkCustomerMapping: (mappingId: string, customerId: string) =>
    apiClient.put(`/tms/customer-mappings/${mappingId}`, { customerId }),
  getUnmappedBookings: (params?: any) =>
    apiClient.get('/tms/unmapped-bookings', { params }),
  searchCustomersForMapping: (query: string) =>
    apiClient.get('/tms/customer-search', { params: { q: query } }),
  createCustomerFromMapping: (mappingId: string, data: any) =>
    apiClient.post(`/tms/customer-mappings/${mappingId}/create-customer`, data),
  getCustomerTemplates: (customerId: string, region = 'dk') =>
    apiClient.get(`/tms/customer/${customerId}/templates`, { params: { region } }),
  getUnmappedOffers: (params?: any) =>
    apiClient.get('/tms/unmapped-offers', { params }),
  linkOfferMapping: (mappingId: string, customerId: string) =>
    apiClient.put(`/tms/offer-mappings/${mappingId}`, { customerId }),
  createCustomerFromOfferMapping: (mappingId: string, data: any) =>
    apiClient.post(`/tms/offer-mappings/${mappingId}/create-customer`, data),
  getAutoMatchSuggestions: (params?: any) =>
    apiClient.get('/tms/auto-match-suggestions', { params }),
  getTransportById: (id: string) =>
    apiClient.get(`/tms/transports/${id}`),
};

// Customer Merge API
export const customerMergeApi = {
  getCandidates: (params?: { page?: number; limit?: number; confidence?: string }) =>
    apiClient.get('/customer-merge/candidates', { params }),
  executeMerge: (primaryId: string, secondaryId: string) =>
    apiClient.post('/customer-merge/execute', { primaryId, secondaryId }),
  dismissMerge: (customerIdA: string, customerIdB: string) =>
    apiClient.post('/customer-merge/dismiss', { customerIdA, customerIdB }),
  makeBranch: (primaryId: string, secondaryId: string) =>
    apiClient.post('/customer-merge/make-branch', { primaryId, secondaryId }),
};

// Report API
export const reportApi = {
  getDashboard: () =>
    apiClient.get('/reports/dashboard'),
  getPipeline: () =>
    apiClient.get('/reports/pipeline'),
  getPerformance: (params?: any) =>
    apiClient.get('/reports/performance', { params }),
  getPerformanceDetail: (userId: string, params?: any) =>
    apiClient.get(`/reports/performance/${userId}`, { params }),
  getLeadReport: (params?: any) =>
    apiClient.get('/reports/leads', { params }),
  getActivityReport: (params?: any) =>
    apiClient.get('/reports/activities', { params }),
  getConversionReport: (params?: any) =>
    apiClient.get('/reports/conversions', { params }),
  getSegmentReport: () =>
    apiClient.get('/reports/segments'),
  exportOffersCsv: (params?: any) =>
    apiClient.get('/reports/offers/export', { params, responseType: 'blob' }),
};

// Offer attachments (files dropped onto an offer)
export const offerAttachmentApi = {
  list: (customerId: string, offerId: string) =>
    apiClient.get(`/customers/${customerId}/offers/${offerId}/attachments`),
  upload: (customerId: string, offerId: string, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    return apiClient.post(`/customers/${customerId}/offers/${offerId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * Fetches the download. Production (S3) answers JSON { url } with a presigned link;
   * local storage streams the file. Both are handled here and opened in a new tab.
   */
  download: async (customerId: string, offerId: string, attachmentId: string, fileName: string) => {
    const res = await apiClient.get(
      `/customers/${customerId}/offers/${offerId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    const contentType: string = res.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await (res.data as Blob).text();
      const parsed = JSON.parse(text);
      const url = parsed?.data?.url;
      if (url) window.open(url, '_blank', 'noopener');
      return;
    }
    const objectUrl = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  },
  delete: (customerId: string, offerId: string, attachmentId: string) =>
    apiClient.delete(`/customers/${customerId}/offers/${offerId}/attachments/${attachmentId}`),
};
